/* eslint-disable react/prop-types */
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { trackEvent } from '../../../utils/analytics';
import {
  buildDiscoveryClickAnalytics,
  buildDiscoveryLinks,
  getDiscoveryAttributes,
  normalizeDiscoveryItems,
} from './styleDiscovery.js';
import './StyleDiscoverySection.css';

const CATEGORY_GLYPHS = {
  top: 'TOP',
  bottom: 'BTM',
  outer: 'OUT',
  shoes: 'SHO',
  accessory: 'ACC',
  eyewear: 'EYE',
};

export default function StyleDiscoverySection({ items, market, marketResolved, isBeta }) {
  const { t, i18n } = useTranslation();
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);
  const sectionTrackedRef = useRef(false);
  const trackedCardsRef = useRef(new Set());
  const language = i18n.resolvedLanguage || i18n.language || 'ko';
  const visibleItems = useMemo(() => normalizeDiscoveryItems(items, language), [items, language]);
  const discoveryKey = visibleItems.map((item) => `${item.id}:${item.category}:${item.query}`).join('|');
  const analyticsMarket = marketResolved ? market : null;

  useEffect(() => {
    sectionTrackedRef.current = false;
    trackedCardsRef.current = new Set();
  }, [discoveryKey]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        if (entry.target === section && !sectionTrackedRef.current) {
          sectionTrackedRef.current = true;
          trackEvent('style_discovery_section_view', {
            analysis_type: 'outfit',
            card_count: visibleItems.length,
            market: analyticsMarket || 'UNKNOWN',
            source: 'style_check_result',
            is_beta: Boolean(isBeta),
          });
        }
        const cardIndex = Number(entry.target.dataset.discoveryCardIndex);
        if (!Number.isInteger(cardIndex) || trackedCardsRef.current.has(cardIndex)) return;
        const item = visibleItems[cardIndex];
        if (!item) return;
        trackedCardsRef.current.add(cardIndex);
        trackEvent('style_discovery_card_view', {
          analysis_type: 'outfit',
          category: item.category,
          item_type: item.item_type,
          position: cardIndex + 1,
          has_example_image: false,
          market: analyticsMarket || 'UNKNOWN',
          recommendation_source: item.recommendation_source,
          is_sponsored: item.is_sponsored,
          source_action: item.source_action,
        });
      });
    }, { threshold: 0.25 });

    observer.observe(section);
    cardRefs.current.slice(0, visibleItems.length).forEach((card) => {
      if (card) observer.observe(card);
    });
    return () => observer.disconnect();
  }, [analyticsMarket, discoveryKey, isBeta, visibleItems]);

  if (!visibleItems.length) return null;

  return (
    <section ref={sectionRef} className="style-discovery-section" aria-labelledby="style-discovery-title">
      <header className="style-discovery-heading">
        <p>{t('style_check.discovery.eyebrow')}</p>
        <h2 id="style-discovery-title">{t('style_check.discovery.title')}</h2>
        <span>{t('style_check.discovery.description')}</span>
      </header>
      <div className={`style-discovery-grid ${visibleItems.length === 1 ? 'is-single' : ''}`}>
        {visibleItems.map((item, index) => {
          const attributes = getDiscoveryAttributes(item.attributes);
          const links = buildDiscoveryLinks({ query: item.query, market, marketResolved });
          const isAdjustment = item.source_action === 'ADJUST';
          return (
            <article
              key={`${item.id}-${item.category}-${index}`}
              ref={(node) => { cardRefs.current[index] = node; }}
              data-discovery-card-index={index}
              className="style-discovery-card"
            >
              <div className="style-discovery-card-icon" aria-hidden="true">{CATEGORY_GLYPHS[item.category]}</div>
              <div className="style-discovery-card-content">
                <p className="style-discovery-category">
                  {t(isAdjustment ? 'style_check.discovery.adjustment_reference' : 'style_check.discovery.recommended_style')}
                  {' · '}{t(`style_check.discovery.categories.${item.category}`)}
                </p>
                <span className={`style-discovery-action-badge is-${item.source_action.toLowerCase()}`}>
                  {t(`style_check.discovery.judgments.${item.source_action}`)}
                </span>
                <h3 className="style-discovery-name">{item.displayName}</h3>
                <p className="style-discovery-direction">
                  {t(isAdjustment ? 'style_check.discovery.adjustment_description' : 'style_check.discovery.replacement_description')}
                </p>
                {attributes.length > 0 && <p className="style-discovery-attributes">{attributes.join(' · ')}</p>}
                {item.reason && <p className="style-discovery-reason"><strong>{t('style_check.discovery.reason_label')}</strong>{item.reason}</p>}
                {item.expected_impact && <p className="style-discovery-impact"><strong>{t('style_check.discovery.impact_label')}</strong>{item.expected_impact}</p>}
                <div className="style-discovery-actions" aria-label={t('style_check.discovery.explore_similar')}>
                  {links.map((provider) => (
                    <a
                      key={provider.id}
                      href={provider.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`style-discovery-link style-discovery-link-${provider.id}`}
                      aria-label={t(`style_check.discovery.aria.${provider.id}`, { name: item.displayName })}
                      onClick={() => trackEvent('style_discovery_click', buildDiscoveryClickAnalytics({
                        providerId: provider.id,
                        item,
                        position: index + 1,
                        market: analyticsMarket,
                      }))}
                    >
                      {t(isAdjustment
                        ? `style_check.discovery.adjustment_actions.${provider.id}`
                        : `style_check.discovery.actions.${provider.id}`)}
                      <span aria-hidden="true">↗</span>
                    </a>
                  ))}
                </div>
                <small className="style-discovery-notice">{t('style_check.discovery.external_notice')}</small>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
